import { clsx } from 'clsx';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
}

export function Skeleton({
  className,
  variant = 'text',
  width,
  height,
}: SkeletonProps) {
  const baseClasses = 'animate-pulse bg-gray-200';

  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  // Default heights for text variant
  if (variant === 'text' && !height) {
    style.height = '1rem';
  }

  return (
    <div
      className={clsx(baseClasses, variantClasses[variant], className)}
      style={style}
      aria-hidden="true"
    />
  );
}

// Pre-built skeleton layouts for common use cases

export function TableRowSkeleton() {
  return (
    <tr>
      <td className="py-3 px-4">
        <div className="space-y-2">
          <Skeleton width="70%" height={14} />
          <Skeleton width="50%" height={12} />
        </div>
      </td>
      <td className="py-3 px-4 hidden md:table-cell">
        <Skeleton width={120} height={12} />
      </td>
      <td className="py-3 px-4">
        <Skeleton width={60} height={20} variant="rectangular" />
      </td>
      <td className="py-3 px-4">
        <Skeleton width={32} height={32} variant="circular" />
      </td>
    </tr>
  );
}

export function CasesListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            <th className="text-left py-2 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Case
            </th>
            <th className="text-left py-2 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide hidden md:table-cell">
              Created
            </th>
            <th className="text-left py-2 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Status
            </th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {Array.from({ length: rows }).map((_, i) => (
            <TableRowSkeleton key={i} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CardSkeleton() {
  return (
    <div className="card">
      <div className="space-y-3">
        <Skeleton width="60%" height={16} />
        <Skeleton width="100%" height={12} />
        <Skeleton width="80%" height={12} />
      </div>
    </div>
  );
}

export function DashboardStatsSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="stats-card">
          <Skeleton width={48} height={32} />
          <Skeleton width={80} height={14} className="mt-2" />
        </div>
      ))}
    </div>
  );
}

export function FormSkeleton() {
  return (
    <div className="space-y-4">
      <div>
        <Skeleton width={80} height={14} className="mb-2" />
        <Skeleton width="100%" height={40} variant="rectangular" />
      </div>
      <div>
        <Skeleton width={120} height={14} className="mb-2" />
        <Skeleton width="100%" height={120} variant="rectangular" />
      </div>
      <div className="flex gap-3 justify-end">
        <Skeleton width={80} height={36} variant="rectangular" />
        <Skeleton width={100} height={36} variant="rectangular" />
      </div>
    </div>
  );
}
